
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Download, Users, Eye, MousePointerClick, 
  Trash2, ExternalLink, TrendingUp, Activity, Copy, MessageCircle, Save, Check, AlertTriangle, Mail, Monitor, Smartphone, Calendar
} from 'lucide-react';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';
import { lockService } from '../services/lockService';
import { Lock, Lead, LockType } from '../types';
import { Button } from './ui/Button';

const LockDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [lock, setLock] = useState<Lock | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [origin, setOrigin] = useState('');
  
  // Activation State
  const [activationUrl, setActivationUrl] = useState('');
  const [isSavingUrl, setIsSavingUrl] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    // Determine the canonical origin for display
    const host = window.location.hostname;
    const isDev = host.includes('localhost') || host.includes('127.0.0.1') || host.includes('googleusercontent');
    setOrigin(isDev ? window.location.origin : 'https://lockd.in');

    if (id) {
      Promise.all([
        lockService.getLockDetails(id),
        lockService.getLockLeads(id)
      ]).then(([lockData, leadsData]) => {
          setLock(lockData);
          setLeads(leadsData);
          if (lockData?.socialUrl) {
              setActivationUrl(lockData.socialUrl);
          }
          setLoading(false);
      }).catch(() => navigate('/'));
    }
  }, [id, navigate]);

  const handleSaveSocialUrl = async () => {
      if (!lock) return;
      setIsSavingUrl(true);
      try {
          const updatedLock = await lockService.updateLock(lock.id, { 
              socialUrl: activationUrl,
              socialType: 'post' 
          });
          setLock(updatedLock);
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 3000);
      } catch (e) {
          alert('Failed to update post URL');
      } finally {
          setIsSavingUrl(false);
      }
  };

  // Chart Data - LEADS ONLY (Real Data)
  const chartData = useMemo(() => {
    if (!lock) return [];
    
    const days = 7;
    const data = [];
    const now = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const dateStr = date.toLocaleDateString('en-US', { weekday: 'short' });
        
        const dayStart = new Date(date.setHours(0,0,0,0)).getTime();
        const dayEnd = new Date(date.setHours(23,59,59,999)).getTime();
        
        const leadsCount = leads.filter(l => l.createdAt >= dayStart && l.createdAt <= dayEnd).length;
        
        data.push({
            name: dateStr,
            Leads: leadsCount
        });
    }
    return data;
  }, [leads, lock]);

  const handleExportCSV = () => {
      if (!leads.length) return;
      
      const headers = ['Email', 'Date', 'Device Type', 'Lock Title'];
      const rows = leads.map(l => [
          l.email,
          new Date(l.createdAt).toLocaleString(),
          l.deviceType,
          lock?.title || ''
      ]);

      const csvContent = [
          headers.join(','),
          ...rows.map(row => row.join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads-${lock?.slug}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  };

  const handleDelete = async () => {
      if (confirm("Are you sure? All leads associated with this lock will also be deleted.")) {
          if (lock) {
            await lockService.deleteLock(lock.id);
            navigate('/');
          }
      }
  };

  const copyLink = () => {
      if(lock) {
        const url = `${origin}/#/l/${lock.slug}`;
        navigator.clipboard.writeText(url);
        alert("Link copied!");
      }
  };

  const copyEmail = (email: string) => {
    navigator.clipboard.writeText(email);
    // Optional: Toast notification here
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse flex flex-col items-center">
            <div className="h-12 w-12 bg-slate-200 rounded-full mb-4"></div>
            <div className="h-4 w-32 bg-slate-200 rounded"></div>
        </div>
    </div>
  );
  
  if (!lock) return null;

  const conversionRate = lock.viewsCount > 0 
    ? ((lock.leadsCount / lock.viewsCount) * 100).toFixed(1) 
    : '0.0';

  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-20">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button 
                            onClick={() => navigate('/dashboard')} 
                            className="p-2 hover:bg-slate-100 rounded-full transition-colors border border-transparent hover:border-slate-200"
                        >
                            <ArrowLeft className="w-5 h-5 text-slate-600" />
                        </button>
                        <div className="h-8 w-px bg-slate-200 hidden sm:block"></div>
                        <div>
                            <h1 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                                {lock.title}
                                <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-md border ${lock.type === LockType.FILE ? 'bg-blue-50 border-blue-100 text-blue-600' : 'bg-purple-50 border-purple-100 text-purple-600'}`}>
                                    {lock.type}
                                </span>
                            </h1>
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                                <span className="font-mono">{lock.slug}</span>
                                <span className="text-slate-300">•</span>
                                <button onClick={copyLink} className="hover:text-slate-900 flex items-center transition-colors">
                                    Copy Link <Copy className="w-3 h-3 ml-1" />
                                </button>
                                <span className="text-slate-300">•</span>
                                <a 
                                    href={`${origin}/#/l/${lock.slug}`} 
                                    target="_blank" 
                                    rel="noreferrer"
                                    className="hover:text-slate-900 flex items-center transition-colors"
                                >
                                    Visit <ExternalLink className="w-3 h-3 ml-1" />
                                </a>
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="secondary" onClick={handleDelete} className="h-9 px-3 text-red-600 hover:bg-red-50 border-red-100 hover:border-red-200">
                            <Trash2 className="w-4 h-4" />
                        </Button>
                        <Button variant="secondary" onClick={handleExportCSV} disabled={leads.length === 0} className="h-9 text-sm">
                            <Download className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Export CSV</span>
                        </Button>
                    </div>
                </div>
            </div>
        </div>

        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
            
            {/* Social Activation Section */}
            {lock.socialUrl && (
                <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-6 rounded-[24px] border ${lock.socialType === 'profile' ? 'bg-orange-50 border-orange-200 ring-4 ring-orange-50' : 'bg-white border-slate-200 shadow-sm'}`}
                >
                    <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${lock.socialType === 'profile' ? 'bg-orange-100 text-orange-600' : 'bg-blue-50 text-blue-600'}`}>
                            {lock.socialType === 'profile' ? <AlertTriangle className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
                        </div>
                        <div className="flex-1">
                            <h3 className={`text-lg font-bold ${lock.socialType === 'profile' ? 'text-orange-900' : 'text-slate-900'}`}>
                                {lock.socialType === 'profile' ? '⚠️ Post Activation Required' : 'Engagement Link Active'}
                            </h3>
                            <p className={`text-sm mb-4 ${lock.socialType === 'profile' ? 'text-orange-800' : 'text-slate-500'}`}>
                                {lock.socialType === 'profile'
                                    ? "This lock is currently in Fallback Mode using your Profile URL. To maximize viral reach, you must paste the specific Post URL below once published."
                                    : "Users are redirected to this Post URL to comment before unlocking content."}
                            </p>
                            
                            <div className="flex flex-col sm:flex-row gap-3 max-w-3xl">
                                <input 
                                    value={activationUrl}
                                    onChange={(e) => setActivationUrl(e.target.value)}
                                    placeholder="Paste your Tweet, LinkedIn, or Instagram post URL here..."
                                    className="flex-1 px-4 py-3 rounded-xl border border-slate-200 focus:border-slate-900 focus:ring-slate-900 focus:outline-none bg-white transition-shadow"
                                />
                                <Button 
                                    onClick={handleSaveSocialUrl} 
                                    isLoading={isSavingUrl}
                                    className={`${saveSuccess ? 'bg-green-600 hover:bg-green-700' : 'bg-slate-900'} h-12`}
                                >
                                    {saveSuccess ? <><Check className="w-4 h-4 mr-2" /> Updated!</> : <><Save className="w-4 h-4 mr-2" /> Activate Post</>}
                                </Button>
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white p-6 rounded-[24px] border border-slate-200 shadow-sm relative overflow-hidden group"
                >
                    <div className="flex justify-between items-start mb-4">
                        <div className="p-2 bg-blue-50 rounded-xl">
                            <Eye className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex items-center text-green-600 text-xs font-bold bg-green-50 px-2 py-1 rounded-full">
                            <TrendingUp className="w-3 h-3 mr-1" /> Live
                        </div>
                    </div>
                    <div className="text-3xl font-bold text-slate-900 tracking-tight mb-1">{lock.viewsCount}</div>
                    <div className="text-sm text-slate-500 font-medium">Total Views</div>
                    <div className="absolute -right-6 -bottom-6 w-24 h-24 bg-blue-50 rounded-full opacity-0 group-hover:opacity-50 transition-opacity"></div>
                </motion.div>

                <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-white p-6 rounded-[24px] border border-slate-200 shadow-sm relative overflow-hidden group"
                >
                    <div className="flex justify-between items-start mb-4">
                         <div className="p-2 bg-slate-900 rounded-xl">
                            <Users className="w-5 h-5 text-white" />
                        </div>
                         <div className="flex items-center text-green-600 text-xs font-bold bg-green-50 px-2 py-1 rounded-full">
                            <TrendingUp className="w-3 h-3 mr-1" /> +24%
                        </div>
                    </div>
                    <div className="text-3xl font-bold text-slate-900 tracking-tight mb-1">{leads.length}</div>
                    <div className="text-sm text-slate-500 font-medium">Leads Captured</div>
                     <div className="absolute -right-6 -bottom-6 w-24 h-24 bg-slate-100 rounded-full opacity-0 group-hover:opacity-50 transition-opacity"></div>
                </motion.div>

                <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white p-6 rounded-[24px] border border-slate-200 shadow-sm relative overflow-hidden group"
                >
                    <div className="flex justify-between items-start mb-4">
                        <div className="p-2 bg-purple-50 rounded-xl">
                            <MousePointerClick className="w-5 h-5 text-purple-600" />
                        </div>
                         <div className="flex items-center text-slate-400 text-xs font-bold bg-slate-50 px-2 py-1 rounded-full">
                            <Activity className="w-3 h-3 mr-1" /> Avg
                        </div>
                    </div>
                    <div className="text-3xl font-bold text-slate-900 tracking-tight mb-1">{conversionRate}%</div>
                    <div className="text-sm text-slate-500 font-medium">Conversion Rate</div>
                     <div className="absolute -right-6 -bottom-6 w-24 h-24 bg-purple-50 rounded-full opacity-0 group-hover:opacity-50 transition-opacity"></div>
                </motion.div>
            </div>

            {/* Chart Section */}
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white p-6 sm:p-8 rounded-[32px] border border-slate-200 shadow-sm"
            >
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h3 className="text-lg font-bold text-slate-900">Lead Growth</h3>
                        <p className="text-sm text-slate-500">Emails captured over the last 7 days</p>
                    </div>
                </div>
                
                <div className="h-[300px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                            <defs>
                                <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#0f172a" stopOpacity={0.1}/>
                                    <stop offset="95%" stopColor="#0f172a" stopOpacity={0}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} dy={10} />
                            <YAxis axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} />
                            <Tooltip 
                                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}
                                itemStyle={{ fontSize: '12px', fontWeight: 600 }}
                            />
                            <Area type="monotone" dataKey="Leads" stroke="#0f172a" strokeWidth={3} fillOpacity={1} fill="url(#colorLeads)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </motion.div>

            {/* Leads Table Section */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
            >
                <div className="flex items-center justify-between mb-4 px-2">
                     <h3 className="text-lg font-bold text-slate-900">Recent Leads</h3>
                     <span className="text-xs font-medium bg-slate-100 text-slate-500 px-2 py-1 rounded-full">{leads.length} total</span>
                </div>

                <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-slate-100 bg-slate-50/50">
                                    <th className="p-5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</th>
                                    <th className="p-5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                                    <th className="p-5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Device</th>
                                    <th className="p-5 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {leads.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="p-12 text-center text-slate-400">
                                            <Mail className="w-8 h-8 mx-auto mb-2 opacity-20" />
                                            No leads captured yet. Share your link to get started.
                                        </td>
                                    </tr>
                                ) : (
                                    leads.slice(0, 50).map((lead) => (
                                        <tr key={lead.id} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="p-5">
                                                <div className="font-medium text-slate-900">{lead.email}</div>
                                            </td>
                                            <td className="p-5 text-sm text-slate-500">
                                                <div className="flex items-center gap-1.5">
                                                    <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                                    {new Date(lead.createdAt).toLocaleDateString()} 
                                                    <span className="text-slate-300">|</span>
                                                    {new Date(lead.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                                </div>
                                            </td>
                                            <td className="p-5">
                                                 <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${lead.deviceType === 'mobile' ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-600'}`}>
                                                    {lead.deviceType === 'mobile' ? <Smartphone className="w-3 h-3" /> : <Monitor className="w-3 h-3" />}
                                                    {lead.deviceType === 'mobile' ? 'Mobile' : 'Desktop'}
                                                 </span>
                                            </td>
                                            <td className="p-5 text-right">
                                                <button onClick={() => copyEmail(lead.email)} className="text-slate-400 hover:text-slate-900 transition-colors p-1">
                                                    <Copy className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    {leads.length > 50 && (
                        <div className="p-4 text-center border-t border-slate-100 text-sm text-slate-500">
                            Showing recent 50. Export CSV to see all.
                        </div>
                    )}
                </div>
            </motion.div>
        </main>
    </div>
  );
};

export default LockDetail;
